// Drag-to-dim policy for a lit light key — pure helpers, no React, so
// the gesture rules can be unit-tested rather than eyeballed on a wall
// iPad. Sibling of music/volume-drag.js.
//
// The gesture, as agreed on the 2026-10 redesign: a tap on a key
// switches the light; a sideways drag across a lit key sets its level,
// snapping to 10%, with the fill visible only while the finger is down.
// One service call goes out, on release. Z-Wave and Zigbee dimmers queue
// commands, and streaming a level per pointermove is how the old blinds
// slider confused its motors (see the SHADES note in direction-a.jsx).
//
// The rejected May 2026 slider failed on size (a 3px track) and
// precision. A 44px-tall key is the target here, and the 10% snap means
// nobody has to land on 37%.

// Sideways travel before a press stops being a tap and becomes a drag.
// Below this a wet or slightly sliding tap still switches the light.
export const DRAG_COMMIT_PX = 10;

// Levels a drag can set. The floor is 10, not 0: switching off is the
// tap's job, and a drag that ends at the far left should leave the light
// dim rather than surprise anyone by turning it off.
export const LEVEL_STEP = 10;
export const LEVEL_MIN = 10;
export const LEVEL_MAX = 100;

// Classify a move from the press point. 'drag' once sideways travel
// passes the threshold and dominates; 'scroll' once vertical travel
// does (the page owns it from then on, so a phone can still scroll past
// a column of keys); null while it is still undecided.
export function gestureIntent(dx, dy, threshold = DRAG_COMMIT_PX) {
  const ax = Math.abs(dx), ay = Math.abs(dy);
  if (ax >= threshold && ax > ay) return 'drag';
  if (ay >= threshold && ay >= ax) return 'scroll';
  return null;
}

// The level under the finger: its position across the whole key, as a
// share of the key's width, snapped to the step and clamped.
export function levelAt(clientX, left, width) {
  if (!(width > 0)) return LEVEL_MIN;
  const raw = ((clientX - left) / width) * 100;
  const snapped = Math.round(raw / LEVEL_STEP) * LEVEL_STEP;
  return Math.max(LEVEL_MIN, Math.min(LEVEL_MAX, snapped));
}
